import { useEffect } from 'react';
import { PanelLeft, PanelRight } from 'lucide-react';
import { useLayoutStore } from '../stores/layoutStore';
import { prefetchPanel } from './panelImports';
import { useCanvasChrome } from './CanvasChromeContext';
import type { NavItemConfig } from './navItems';
import type { PanelId } from '../types';

function isNavPanelOpen(
  panelId: string,
  panels: ReturnType<typeof useLayoutStore.getState>['panels'],
): boolean {
  return panels[panelId]?.visible === true;
}

function collapsedNavItemClassName(open: boolean): string {
  const base =
    'w-full flex items-center justify-center py-2 transition-colors relative';
  return open
    ? `${base} text-canvas-primary bg-canvas-primary-tint`
    : `${base} text-canvas-faint hover:text-canvas-primary hover:bg-canvas-primary-tint`;
}

function expandedNavItemClassName(open: boolean): string {
  const base =
    'flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors';
  return open
    ? `${base} bg-canvas-primary-tint text-canvas-primary font-medium`
    : `${base} text-canvas-muted hover:bg-canvas-primary-tint hover:text-canvas-primary`;
}

/**
 * Tenant-driven sidebar. Reads `navItems` and the active `panels` registry
 * from `CanvasChromeContext` — set on `<CanvasShell navItems={...} panels={...}>`.
 * No baked-in career copy; the OSS default registry is just the path of
 * least surprise.
 *
 * `prefetchKey` on a nav item maps it to a `panels` registry key so hover/
 * focus warms the chunk ~100 ms before the click. Items pointing at eager
 * panels (chat, artifacts, voice) leave `prefetchKey` undefined.
 */
export function NavSidebar() {
  const {
    panels,
    togglePanel,
    resetPan,
    navSidebarExpanded,
    setNavSidebarExpanded,
    setNavSidebarItemCount,
    voiceDocked,
  } = useLayoutStore();
  const { navItems, panels: panelRegistry } = useCanvasChrome();
  const collapsed = !navSidebarExpanded;
  const voiceDockActive = voiceDocked && panels.voice?.visible === true;

  useEffect(() => {
    setNavSidebarItemCount(navItems.length);
  }, [navItems.length, setNavSidebarItemCount]);

  const shellClassName = collapsed
    ? `absolute left-3 top-16 z-40 w-11 bg-canvas-surface border border-canvas-border shadow-sm overflow-hidden py-1.5 pointer-events-auto ${
        voiceDockActive
          ? 'rounded-t-xl rounded-b-none border-b-0'
          : 'rounded-xl'
      }`
    : `absolute left-3 top-16 z-40 w-[210px] bg-canvas-surface border border-canvas-border shadow-sm overflow-hidden pointer-events-auto ${
        voiceDockActive
          ? 'rounded-t-xl rounded-b-none border-b-0'
          : 'rounded-xl'
      }`;

  const handleItemClick = (item: NavItemConfig) => {
    if (!item.panelId) return;
    resetPan();
    togglePanel(item.panelId as PanelId);
  };

  const handlePrefetch = (item: NavItemConfig) => {
    // Prefer explicit `prefetchKey`, fall back to `panelId`. Membership
    // check against the active registry naturally skips eager panels
    // (chat, artifacts) — they don't appear in `panels`. Architect-LOW
    // 2026-04-26 follow-up: removes the silent-no-op footgun when a
    // tenant nav id matches a registry id without redundant `prefetchKey`.
    const key = item.prefetchKey ?? item.panelId;
    if (panelRegistry[key]) prefetchPanel(key, panelRegistry);
  };

  if (collapsed) {
    return (
      <div className={shellClassName}>
        <button
          onClick={() => setNavSidebarExpanded(true)}
          className="w-full flex items-center justify-center py-2 text-canvas-faint hover:text-canvas hover:bg-canvas-surface-subtle transition-colors"
          title="Expand sidebar"
        >
          <PanelRight size={18} />
        </button>
        <div className="border-t border-canvas-border my-1 mx-2" />
        {navItems.map((item) => {
          const open = item.panelId
            ? isNavPanelOpen(item.panelId, panels)
            : false;
          return (
            <button
              key={item.id}
              title={item.label}
              aria-current={open ? 'page' : undefined}
              onClick={() => handleItemClick(item)}
              onPointerEnter={() => handlePrefetch(item)}
              onFocus={() => handlePrefetch(item)}
              className={collapsedNavItemClassName(open)}
            >
              <item.icon size={18} />
              {open ? (
                <span
                  className="absolute bottom-1.5 right-2 w-1.5 h-1.5 rounded-full bg-canvas-primary"
                  aria-hidden
                />
              ) : null}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={shellClassName}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-canvas-border">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-canvas-muted">Menu</span>
        </div>
        <button
          onClick={() => setNavSidebarExpanded(false)}
          className="p-1.5 rounded-lg hover:bg-canvas-surface-subtle text-canvas-faint hover:text-canvas transition-colors"
          title="Collapse sidebar"
        >
          <PanelLeft size={16} />
        </button>
      </div>
      {navItems.map((item) => {
        const open = item.panelId
          ? isNavPanelOpen(item.panelId, panels)
          : false;
        return (
          <div
            key={item.id}
            role="button"
            tabIndex={0}
            aria-current={open ? 'page' : undefined}
            className={expandedNavItemClassName(open)}
            onClick={() => handleItemClick(item)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleItemClick(item);
              }
            }}
            onPointerEnter={() => handlePrefetch(item)}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                open ? 'bg-canvas-primary' : 'bg-transparent'
              }`}
              aria-hidden
            />
            <item.icon
              size={18}
              className={open ? 'text-canvas-primary' : 'text-canvas-faint'}
            />
            <span>{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}
